// Copyright (c) 2025 TrustCloud Corporation - All rights reserved.
//
// This software contains valuable confidential and proprietary information of
// TrustCloud Corporation
//
// Unauthorized use, reproduction, transmission, or distribution of this file and its
// contents is a violation of applicable law.
import React, { useMemo } from 'react';
import { Typography } from '@kintent/glide';

import * as Styles from './FooterV2.styles';
import TrustCloudFooterBranding from '../TrustCloudFooterBranding';
import SocialMediaFooterLinks from '../SocialMediaFooterLinks';
import { useProgramContent } from '../../lib/state';

function createMapFromFooterLinks(footerLinks) {
  return (
    footerLinks?.items.reduce((acc, footerItem) => {
      const { label, url = null, onClick = null } = footerItem;
      return acc.set(label, { url, onClick });
    }, new Map()) ?? new Map()
  );
}

function FooterLinks({ footerMap }) {
  if (footerMap.size === 0) return null;

  return Array.from(footerMap).map(([label, { url, onClick }]) => {
    const dataTestId = `footer-section-link-${label?.replace(/\s/g, '-').toLowerCase()}`;

    let onClickFunction = null;
    const onClickFunctionName = onClick?.match(/(\w+\.?)+/);
    if (onClickFunctionName != null && onClickFunctionName[0] === onClick) {
      // eslint-disable-next-line no-new-func
      onClickFunction = new Function(`try { ${onClick}() } catch (error) { console.error(error)}`);
    }

    return (
      <li key={label}>
        <Typography
          as={onClick ? 'p' : 'a'}
          className="footer-item"
          role={onClick ? 'button' : 'link'}
          data-testid={dataTestId}
          strength="60%"
          level="8"
          {...(url && { href: url, target: '_blank' })}
          {...(onClickFunction && { onClick: onClickFunction })}
        >
          {label}
        </Typography>
      </li>
    );
  });
}

function FooterV2() {
  const [{ footerCopy, footerLinks = null, footerStyle }] = useProgramContent();

  // Extract footer styles if they exist
  const footerCopyStyle = footerStyle?.footerCopy ?? {};

  // Create a map from footer links
  const footerMap = useMemo(() => createMapFromFooterLinks(footerLinks), [footerLinks]);

  return (
    <Styles.Footer data-testid="footer-section">
      <Styles.FooterContainer>
        {footerCopy && (
          <Typography
            as="p"
            strength="60%"
            level="8"
            style={footerCopyStyle}
            className="footer-section-copy"
          >
            {footerCopy}
          </Typography>
        )}
        <Styles.UnstyledList>
          <FooterLinks footerMap={footerMap} />
        </Styles.UnstyledList>
        <TrustCloudFooterBranding />
        <SocialMediaFooterLinks />
      </Styles.FooterContainer>
    </Styles.Footer>
  );
}

export default FooterV2;
